import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartItemsAPI } from '../services/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    return token && user && user !== 'undefined';
  };

  // Load cart from server or localStorage
  const loadCart = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isAuthenticated()) {
        // Load from server
        const response = await cartItemsAPI.getAll();
        setCartItems(response.data);
      } else {
        // Load from localStorage
        const savedCart = localStorage.getItem('cart');
        if (savedCart && savedCart !== 'undefined') {
          try {
            const parsedCart = JSON.parse(savedCart);
            setCartItems(parsedCart);
          } catch (error) {
            console.error('Error parsing cart from localStorage:', error);
            localStorage.removeItem('cart');
          }
        }
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      // Fallback to localStorage
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, []);


  // Load cart on mount
  useEffect(() => {
    loadCart();
  }, [loadCart]);

  // Only save to localStorage for non-authenticated users
  useEffect(() => {
    if (isInitialized && !isAuthenticated()) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isInitialized]);

  const addToCart = async (product) => {
    try {
      if (isAuthenticated()) {
        // Add to server
        const response = await cartItemsAPI.add(product.id, 1);
        // Update local state with the response
        if (response.data) {
          setCartItems(prevItems => {
            const existingItem = prevItems.find(item => item.product_id === product.id);
            
            if (existingItem) {
              return prevItems.map(item =>
                item.product_id === product.id
                  ? { ...item, quantity: item.quantity + 1 }
                  : item
              );
            } else {
              return [...prevItems, response.data];
            }
          });
        }
      } else {
        // Add to local state
        setCartItems(prevItems => {
          const existingItem = prevItems.find(item => item.product_id === product.id);
          
          if (existingItem) {
            return prevItems.map(item =>
              item.product_id === product.id
                ? { ...item, quantity: item.quantity + 1 }
                : item
            );
          } else {
            return [...prevItems, { 
              id: Date.now(), // temporary ID for local storage
              product_id: product.id,
              quantity: 1,
              product: product
            }];
          }
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      if (isAuthenticated()) {
        const cartItem = cartItems.find(item => item.product_id === productId);
        if (cartItem) {
          if (cartItem.quantity > 1) {
            await cartItemsAPI.update(cartItem.id, cartItem.quantity - 1);
            // Update local state
            setCartItems(prevItems =>
              prevItems.map(item =>
                item.product_id === productId
                  ? { ...item, quantity: item.quantity - 1 }
                  : item
              )
            );
          } else {
            await cartItemsAPI.remove(cartItem.id);
            // Update local state
            setCartItems(prevItems => prevItems.filter(item => item.product_id !== productId));
          }
        }
      } else {
        setCartItems(prevItems => {
          const existingItem = prevItems.find(item => item.product_id === productId);
          
          if (existingItem && existingItem.quantity > 1) {
            return prevItems.map(item =>
              item.product_id === productId
                ? { ...item, quantity: item.quantity - 1 }
                : item
            );
          } else {
            return prevItems.filter(item => item.product_id !== productId);
          }
        });
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const removeItemCompletely = async (productId) => {
    try {
      if (isAuthenticated()) {
        const cartItem = cartItems.find(item => item.product_id === productId);
        if (cartItem) {
          await cartItemsAPI.remove(cartItem.id);
          // Update local state
          setCartItems(prevItems => prevItems.filter(item => item.product_id !== productId));
        }
      } else {
        setCartItems(prevItems => prevItems.filter(item => item.product_id !== productId));
      }
    } catch (error) {
      console.error('Error removing item completely:', error);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      if (quantity <= 0) {
        await removeItemCompletely(productId);
        return;
      }

      if (isAuthenticated()) {
        const cartItem = cartItems.find(item => item.product_id === productId);
        if (cartItem) {
          await cartItemsAPI.update(cartItem.id, quantity);
          // Update local state
          setCartItems(prevItems =>
            prevItems.map(item =>
              item.product_id === productId
                ? { ...item, quantity }
                : item
            )
          );
        }
      } else {
        setCartItems(prevItems =>
          prevItems.map(item =>
            item.product_id === productId
              ? { ...item, quantity }
              : item
          )
        );
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const getItemQuantity = (productId) => {
    const item = cartItems.find(item => item.product_id === productId);
    return item ? item.quantity : 0;
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      const price = item.product?.price || item.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const clearCart = async () => {
    try {
      if (isAuthenticated()) {
        await cartItemsAPI.clear();
        // Update local state
        setCartItems([]);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  // Sync local cart with server when user logs in
  const syncCartOnLogin = useCallback(async () => {
    if (isAuthenticated()) {
      const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
      if (localCart.length > 0) {
        try {
          // Transform local cart data to the format expected by the backend
          const cartItemsToSync = localCart.map(item => ({
            product_id: item.product_id || item.id,
            quantity: item.quantity || 1
          }));
          
          const response = await cartItemsAPI.sync(cartItemsToSync);
          localStorage.removeItem('cart'); // Clear local cart after sync
          // Update local state with synced data
          setCartItems(response.data);
        } catch (error) {
          console.error('Error syncing cart on login:', error);
          // If sync fails, load from server
          await loadCart();
        }
      } else {
        // No local cart, just load from server
        await loadCart();
      }
    }
  }, [loadCart]);

  const value = {
    cartItems,
    isLoading,
    isInitialized,
    addToCart,
    removeFromCart,
    removeItemCompletely,
    updateQuantity,
    getItemQuantity,
    getTotalItems,
    getTotalPrice,
    clearCart,
    syncCartOnLogin,
    loadCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
